import React, { useState, useRef } from 'react';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Zap, Globe, Lightbulb } from 'lucide-react';
const WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/mmuChs2XqH3KvF4a7fk2/webhook-trigger/laO0oSsIulBjUpiOERDf';
const UPLOADCARE_PUB_KEY = 'fff7c1111ac2fc5b8306';
async function uploadToUploadcare(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('UPLOADCARE_PUB_KEY', UPLOADCARE_PUB_KEY);
  formData.append('UPLOADCARE_STORE', '1');
  formData.append('file', file);
  const response = await fetch('https://upload.uploadcare.com/base/', {
    method: 'POST',
    body: formData
  });
  if (!response.ok) throw new Error('File upload failed');
  const result = await response.json();
  return `https://ucarecdn.com/${result.file}/`;
}
export function Careers() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    // Validate file manually
    const fileInput = fileInputRef.current;
    if (!fileInput?.files?.[0]) {
      setError('Please upload your resume (PDF).');
      setIsSubmitting(false);
      return;
    }
    // Collect form data first
    const formData = new FormData(e.currentTarget);
    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      if (typeof value === 'string') data[key] = value;
    });
    // Try uploading PDF to Uploadcare
    try {
      const resumeUrl = await uploadToUploadcare(fileInput.files[0]);
      data.resumeUrl = resumeUrl;
    } catch (uploadErr) {
      // If upload fails, continue without resume URL but note the failure
      data.resumeUrl = 'Upload failed - please request resume separately';
    }
    // Send to webhook via query params (bypasses all CORS restrictions)
    try {
      const params = new URLSearchParams(data);
      const img = new Image();
      img.src = `${WEBHOOK_URL}?${params.toString()}`;
      setIsSubmitting(false);
      setIsSuccess(true);
    } catch (err) {
      setIsSubmitting(false);
      setError('Failed to submit application. Please try again.');
    }
  };
  return <div className="w-full pt-20 pb-24 bg-engage-black min-h-screen" data-id="element-91">
      {/* Hero */}
      <section className="pt-16 md:pt-24 pb-8 md:pb-12 text-center px-4" data-id="element-92">
        <div className="max-w-4xl mx-auto" data-id="element-93">
          <h1 className="text-5xl md:text-6xl font-black text-engage-white mb-6 tracking-tight" data-id="element-94">
            Careers at Engage Media
          </h1>
          <p className="text-xl text-engage-yellow font-medium" data-id="element-95">
            We're always looking for top talent. Drop your details below.
          </p>
        </div>
      </section>

      {/* Culture Section */}

      {/* Application Form */}
      <section className="max-w-4xl mx-auto px-4" data-id="element-96">
        <div className="text-center mb-12" data-id="element-97"></div>

        <Card className="!p-8 md:!p-12" data-id="element-98">
          {isSuccess ? <div className="text-center py-16" data-id="element-99">
              <div className="w-20 h-20 bg-engage-yellow/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-100">
                <svg className="w-10 h-10 text-engage-yellow" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-101">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-102" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-103">Application Submitted</h2>
              <p className="text-engage-gray4 text-lg mb-8" data-id="element-104">
                Thanks for applying. If your profile matches what we're looking
                for, our team will be in touch soon.
              </p>
              <Button onClick={() => setIsSuccess(false)} variant="secondary" data-id="element-105">
                Submit Another Application
              </Button>
            </div> : <form onSubmit={handleSubmit} className="space-y-8" data-id="element-106">
              {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-center" data-id="element-107">
                  {error}
                </div>}

              {/* Name Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-108">
                <Input label="First Name" name="firstName" placeholder="Jane" required data-id="element-109" />
                <Input label="Last Name" name="lastName" placeholder="Smith" required data-id="element-110" />
              </div>

              {/* Contact Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-111">
                <Input label="Email Address" name="email" type="email" placeholder="jane@example.com" required data-id="element-112" />
                <Input label="Phone Number (with country code)" name="phone" type="tel" placeholder="+1 (555) 000-0000" required data-id="element-113" />
              </div>

              {/* Country & File */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-114">
                <Input label="Country of Residence" name="country" as="select" required options={[{
              value: 'us',
              label: 'United States'
            }, {
              value: 'uk',
              label: 'United Kingdom'
            }, {
              value: 'ca',
              label: 'Canada'
            }, {
              value: 'au',
              label: 'Australia'
            }, {
              value: 'eu',
              label: 'Europe'
            }, {
              value: 'other',
              label: 'Other'
            }]} data-id="element-115" />
                <div className="flex flex-col space-y-2" data-id="element-116">
                  <label className="text-sm font-medium text-engage-gray5" data-id="element-117">
                    Upload Resume (PDF)
                  </label>
                  <input ref={fileInputRef} type="file" accept=".pdf" className="w-full bg-engage-gray2 border border-engage-gray3 rounded-sm px-4 py-2.5 text-engage-white file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-sm file:font-semibold file:bg-engage-yellow file:text-engage-black hover:file:bg-engage-yellow-hover cursor-pointer" data-id="element-118" />
                </div>
              </div>

              <Input label="Portfolio URL (Optional)" name="portfolioUrl" type="url" placeholder="https://myportfolio.com" data-id="element-119" />

              <Input label="How did you hear about us?" name="howDidYouHear" as="select" required options={[{
            value: 'social',
            label: 'Social Media'
          }, {
            value: 'referral',
            label: 'Referral / Word of Mouth'
          }, {
            value: 'website',
            label: 'Website'
          }, {
            value: 'google',
            label: 'Google'
          }, {
            value: 'other',
            label: 'Other'
          }]} data-id="element-120" />

              {/* Textarea */}
              <Input label="Why do you want to join Engage Media?" name="whyJoin" as="textarea" placeholder="Tell us what makes you a great fit for a performance-driven culture..." required data-id="element-121" />

              {/* Submit */}
              <Button type="submit" variant="primary" fullWidth className="text-lg py-4 mt-8" disabled={isSubmitting} data-id="element-122">
                {isSubmitting ? 'Submitting...' : 'Apply Now'}
              </Button>
            </form>}
        </Card>
      </section>
    </div>;
}